import React, {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import MediaGalleryDetails from './MediaGalleryDetails';
import {
  PopOut,
  Vertical,
  Horizontal,
  Button,
  Rotate90Icon,
  TrashIcon,
  ThemeName,
} from '@taopix/taopix-design-system';
import {hasExpired} from '../../Utils/Asset';
import {AssetMetaInterface} from '@taopix/taopix-core/dist/types/types';
import {PopOutDisplayModes} from '@taopix/taopix-design-system/dist/types/Components/PopOut/PopOut';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';

export type MediaGalleryDetailsDialogProps = {
  isOpen: boolean;
  image?: AssetMetaInterface;
  showUsage: boolean;
  used: number;
  anchorId?: string;
  excludeIdFromClickOutside?: string;
  locale: string;
  onClose: () => void;
  closeEvent: () => void;
  onRotateAsset?: (id: number) => void;
  onDeleteAsset?: (id: number) => void;
};

const MediaGalleryDetailsDialog = ({
  isOpen,
  image,
  used,
  showUsage,
  anchorId,
  excludeIdFromClickOutside,
  locale = 'en-GB',
  closeEvent,
  onClose,
  onRotateAsset,
  onDeleteAsset,
  ...props
}: MediaGalleryDetailsDialogProps) => {
  const {t} = useTranslation(['*', 'assetservices']);
  const ui = useUiState();

  useEffect(() => {
    if (!image && isOpen) {
      closeEvent();
    }
  }, [image || false, isOpen]);

  // The details popout will be shown full screen unless an anchor Id has been provided
  let displayMode: PopOutDisplayModes = 'modal';

  if (anchorId) {
    displayMode = 'positioned';
  }

  const expired = image?.link?.expirationDate ? hasExpired(image?.link.expirationDate) : false;

  const contentClasses = classNames('flex', 'flex-col', 'p-md', 'space-y-md');

  return (
    <PopOut
      id={'media-details-dialog'}
      anchorId={anchorId}
      anchorOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Right}}
      transformOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Left}}
      displayMode={displayMode}
      open={isOpen}
      popDirection={displayMode === 'modal' ? 'up' : 'right'}
      offset={ui.state.isSmallScreen ? {x: 10, y: 0} : {x: 0, y: 0}}
      onClose={onClose}
      excludeIdFromClickOutside={excludeIdFromClickOutside}
      edgeBehaviour={'move'}
      focusFirstElement
      contentTheme={ThemeName.Popouts}
      role="dialog"
      trapFocus
      aria-labelledby="media-details-dialog-title"
    >
      <h1 className="sr-only" id="media-details-dialog-title">
        {t('assetServices:str_TitlePicturePreviewAndDetails')}
      </h1>
      <div className={contentClasses}>
        <MediaGalleryDetails
          image={image}
          used={showUsage ? used : undefined}
          displayMode={displayMode}
          expired={expired}
          locale={locale}
        />
        <div className="flex flex-wrap space-x-sm">
          <Button
            buttonStyle="negative"
            size="small"
            label={t('str_ButtonClose')}
            onClick={closeEvent}
            className={'mr-auto'}
          />

          {!expired && onRotateAsset && (
            <Button
              label={t('str_ButtonRotate')}
              buttonStyle="tertiary"
              size="small"
              startIcon={<Rotate90Icon />}
              onClick={e => onRotateAsset(image?.id as number)}
            />
          )}
          {onDeleteAsset && (
            <Button
              label={t('str_ButtonDelete')}
              buttonStyle="tertiary"
              size="small"
              startIcon={<TrashIcon />}
              onClick={e => onDeleteAsset(image?.id as number)}
              disabled={used > 0}
            />
          )}
        </div>
      </div>
    </PopOut>
  );
};

export default MediaGalleryDetailsDialog;
